/*
  <custom-size> — standard/custom dimension toggle for made-to-order desks.

  Standard mode = the normal buy box. Custom mode = a QUOTE flow (no per-size pricing):
  it reveals Width×Depth inputs and a WhatsApp "request a quote" button prefilled with the
  product + chosen dimensions. While custom mode is active we add `csize-quote` to <html>
  (CSS hides the price, tax, delivery line, main add-to-cart AND the sticky add-to-cart),
  and a capture-phase submit guard blocks any add-to-cart on the product form — so the
  standard variant can never be purchased while the customer is configuring a custom size.

  The W×D inputs are NOT form-associated; they exist only to build the WhatsApp message.

  Guarded (no double-define), defer-loaded.
*/
if (!customElements.get('custom-size')) {
  customElements.define(
    'custom-size',
    class CustomSize extends HTMLElement {
      connectedCallback() {
        this.formId = this.dataset.formId || '';
        this.phone = (this.dataset.quotePhone || '').replace(/[^0-9]/g, '');
        this.productName = this.dataset.quoteProduct || '';
        this.modes = Array.from(this.querySelectorAll('[data-csize-mode]'));
        this.panel = this.querySelector('[data-csize-fields]');
        this.numInputs = Array.from(this.querySelectorAll('[data-csize-num]'));
        this.errorEl = this.querySelector('[data-csize-error]');
        this.quoteBtn = this.querySelector('[data-csize-quote]');
        this.mode = 'standard';

        this.onModeClick = this.onModeClick.bind(this);
        this.modes.forEach((b) => b.addEventListener('click', this.onModeClick));

        this.onInput = this.onInput.bind(this);
        this.numInputs.forEach((i) => i.addEventListener('input', this.onInput));

        this.onQuoteClick = this.onQuoteClick.bind(this);
        if (this.quoteBtn) this.quoteBtn.addEventListener('click', this.onQuoteClick);

        // Capture-phase guard on the product form so we run before product-form.js.
        this.form = this.formId ? document.getElementById(this.formId) : null;
        this.onSubmit = this.onSubmit.bind(this);
        if (this.form) this.form.addEventListener('submit', this.onSubmit, true);

        this.setMode('standard');
      }

      disconnectedCallback() {
        this.modes.forEach((b) => b.removeEventListener('click', this.onModeClick));
        this.numInputs.forEach((i) => i.removeEventListener('input', this.onInput));
        if (this.quoteBtn) this.quoteBtn.removeEventListener('click', this.onQuoteClick);
        if (this.form) this.form.removeEventListener('submit', this.onSubmit, true);
        document.documentElement.classList.remove('csize-quote');
      }

      onModeClick(e) {
        this.setMode(e.currentTarget.getAttribute('data-csize-mode'));
      }

      onInput() {
        this.clearError();
        this.refreshQuote();
      }

      setMode(mode) {
        this.mode = mode === 'custom' ? 'custom' : 'standard';
        const custom = this.mode === 'custom';
        this.modes.forEach((b) => {
          const active = b.getAttribute('data-csize-mode') === this.mode;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        if (this.panel) this.panel.hidden = !custom;
        // Quote mode hides price / tax / delivery / main + sticky add-to-cart (CSS).
        document.documentElement.classList.toggle('csize-quote', custom);
        this.clearError();
        this.refreshQuote();
      }

      values() {
        const out = {};
        this.numInputs.forEach((i) => {
          out[i.getAttribute('data-csize-dim') || i.name] = i;
        });
        return out;
      }

      isValid() {
        return this.numInputs.every((i) => {
          const v = parseFloat(i.value);
          const min = parseFloat(i.min);
          const max = parseFloat(i.max);
          if (!i.value || isNaN(v)) return false;
          if (!isNaN(min) && v < min) return false;
          if (!isNaN(max) && v > max) return false;
          return true;
        });
      }

      // Build the WhatsApp deep link from the current dimensions.
      buildHref() {
        const v = this.values();
        const w = v.w ? v.w.value : '';
        const d = v.d ? v.d.value : '';
        const msg =
          'Bună! Aș dori o ofertă pentru ' +
          (this.productName || 'biroul personalizat') +
          ' în dimensiune personalizată: ' +
          w +
          ' cm lățime × ' +
          d +
          ' cm adâncime. Mulțumesc!';
        const base = this.phone ? 'https://wa.me/' + this.phone : 'https://wa.me/';
        return base + '?text=' + encodeURIComponent(msg);
      }

      // Keep the quote button's href + enabled state in sync with the inputs.
      refreshQuote() {
        if (!this.quoteBtn) return;
        const ok = this.mode === 'custom' && this.isValid();
        if (ok) {
          this.quoteBtn.href = this.buildHref();
          this.quoteBtn.setAttribute('aria-disabled', 'false');
        } else {
          this.quoteBtn.href = '#';
          this.quoteBtn.setAttribute('aria-disabled', 'true');
        }
      }

      onQuoteClick(e) {
        if (!this.isValid()) {
          e.preventDefault();
          this.showError();
          if (this.numInputs[0]) this.numInputs[0].focus();
          return;
        }
        // Ensure the freshest dimensions are encoded at click time.
        this.quoteBtn.href = this.buildHref();
      }

      // No custom size is ever added to cart — block the product form while in custom mode.
      onSubmit(e) {
        if (this.mode !== 'custom') return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.showError();
        if (this.numInputs[0]) this.numInputs[0].focus();
      }

      showError() {
        if (this.errorEl) this.errorEl.hidden = false;
        this.classList.add('is-invalid');
      }

      clearError() {
        if (this.errorEl) this.errorEl.hidden = true;
        this.classList.remove('is-invalid');
      }
    }
  );
}
